import { Button } from '@mui/material';
import * as i18n from 'i18next';
import { orderBy } from 'lodash';
import moment from 'moment';
import { useEffect, useMemo, useState } from 'react';
import DOMPurify from 'dompurify';
import { useDispatch, useSelector } from 'react-redux';
import { checkForNewVersion, showPaidProductsModal } from '../../../actions';
import { getAllTrackers } from '../../../actions/trackerActions';
import {
  addTrackingRecord,
  getTrackingRecordsByType,
  updateTrackingRecord,
} from '../../../actions/trackingRecordsActions';
import {
  FREE_TRACKERS,
  MINIMUM_FREE_TRACKER_LIMIT,
  RELATIVE_LINKS_PROD,
  RELATIVE_PAGE_PATHS,
  TRACKER_SUBTYPES,
  TRACKERS_SUB_TYPE,
} from '../../../utils/constants';

import { useHistory, useParams } from 'react-router';
import navigationMethods from '../../../utils/navigationUtils';
import TrackerHistory from '../TrackerHistory';
import { TrackingEditDialog } from '../tracking/TrackingEditDialog';
import { TrackingTimer } from '../tracking/TrackingTimer';
import TrackerRecordNote from '../TrackerRecordNote';
import { MODAL_TYPE } from '../../../models/modalTypes';
import { BREASTS } from '../tracking/models';
import { getTrackerId } from '../../../utils/commonUtils';
import { canAddTrackerRecord } from '../tracking/utils';

export const BreastFeedingTracker = () => {
  const [modal, setModal] = useState(MODAL_TYPE.DEFAULT);
  const isSubscribedToPaidStickers = useSelector(
    (state) => state.authentication.userData.isSubscribedToPaidStickers
  );
  const tracker = useSelector(trackerIdSelector);
  const records = useSelector((state) =>
    orderBy(
      state.trackingRecords.filter((x) => x.subTypeId === tracker.id),
      (x) => new Date(x.date),
      ['desc']
    )
  );

  const breastfeedingDescriptionHtml = useMemo(() => {
    const raw = i18n.t('trackers:trackerDescriptions.breast-feeding', { defaultValue: '' });

    if (!raw) return '';
    return DOMPurify.sanitize(raw, {
      ALLOWED_TAGS: ['a', 'br', 'strong', 'b', 'i', 'em'],
      ALLOWED_ATTR: ['href', 'target', 'rel'],
    });
  }, []);

  const dispatch = useDispatch();
  const history = useHistory();
  const { typeId, recordId } = useParams();

  const recordToEdit = useMemo(() => {
    if (!records || !recordId) {
      return null;
    }
    return records.find((x) => x.id + '' === recordId);
  }, [recordId, records]);

  useEffect(() => {
    dispatch(getAllTrackers());
    const trackerId = getTrackerId(TRACKER_SUBTYPES.BREAST_FEEDING);
    dispatch(getTrackingRecordsByType(trackerId, 0));
    dispatch(checkForNewVersion());
  }, [dispatch]);

  const renderLastRecordDetails = () => {
    const lastRecord = records[0];

    if (!lastRecord) {
      return (
        <div className="text">
          {/* ✅ Description ABOVE "Предишно кърмене" */}
          {breastfeedingDescriptionHtml ? (
            <div
              className="tracker-description"
              style={{ marginBottom: 8 }}
              dangerouslySetInnerHTML={{ __html: breastfeedingDescriptionHtml }}
            />
          ) : null}

          <p>{i18n.t('trackers:breastfeedingScreen:lastFeeding')}</p>
          <div>
            {i18n.t('trackers:breastfeedingScreen:notBreastfeedingYet')}
          </div>
          <a
            href="/analytics?type=feeding"
            style={{ textDecoration: 'underline' }}
          >
            {i18n.t('trackers:breastfeedingScreen:analyticsLink')}
          </a>
        </div>
      );
    }

    const side = i18n.t(
      `trackers:trackingScreen:tracking.${lastRecord.additionalInfo}`
    );
    const beforeTime = moment.duration(
      moment(new Date()).diff(lastRecord.date)
    );
    const days = Math.floor(beforeTime.asDays());
    return (
      <div className="text">
        {/* ✅ Description ABOVE "Предишно кърмене" */}
        {breastfeedingDescriptionHtml ? (
          <div
            className="tracker-description"
            style={{ marginBottom: 8 }}
            dangerouslySetInnerHTML={{ __html: breastfeedingDescriptionHtml }}
          />
        ) : null}

        <p>{i18n.t('trackers:breastfeedingScreen:lastFeeding')}</p>
        <p className="mb-0">
          <strong className="uppercase">{side} </strong>
          {i18n.t('trackers:breastfeedingScreen:breast')}
        </p>
        <div>
          {i18n.t('trackers:breastfeedingScreen:breastfeedingTime', {
            hours: beforeTime.hours(),
            minutes: beforeTime.minutes(),
            days: days
              ? i18n.t('trackers:breastfeedingScreen:days', { days })
              : '',
          })}
        </div>
        <a
          href="/analytics?type=feeding"
          style={{ textDecoration: 'underline' }}
        >
          {i18n.t('trackers:breastfeedingScreen:analyticsLink')}
        </a>
      </div>
    );
  };

  const renderLastRecord = () => {
    return (
      <div className="details">
        <img
          alt="BreastFeedingIcon"
          src={navigationMethods.getSystemImageUrl(tracker.image)}
          width={96}
          height={96}
          style={{
            backgroundColor: 'white',
            borderRadius: 8,
            border: '2px solid #87BA3B',
          }}
        />
        {renderLastRecordDetails()}
      </div>
    );
  };

  const closeRecord = () => {
    setModal(MODAL_TYPE.DEFAULT);
    history.replace(RELATIVE_PAGE_PATHS.BREAST_FEEDING);
  };

  const save = (trackerId, startTime, duration, timeOfTheDay) => {
    dispatch(addTrackingRecord(trackerId, startTime, duration, timeOfTheDay));
  };

  const update = (id, startTime, duration, additionalInfo, notes) => {
    dispatch(
      updateTrackingRecord(id, startTime, duration, additionalInfo, notes)
    );
  };

  const saveRecord = (data) => {
    const { startTime, duration, additionalInfo } = data;
    if (recordToEdit) {
      update(
        recordToEdit.id,
        startTime,
        duration,
        additionalInfo,
        recordToEdit.notes
      );
      history.replace(`${RELATIVE_PAGE_PATHS.BREAST_FEEDING}`);
      return;
    }
    save(tracker.id, startTime, duration, additionalInfo);
  };

  const editRecord = (id) => {
    setModal(MODAL_TYPE.EDIT);
    history.push(`${RELATIVE_PAGE_PATHS.BREAST_FEEDING}/${id}`);
  };

  const openNote = (id) => {
    setModal(MODAL_TYPE.NOTE);
    history.push(`${RELATIVE_PAGE_PATHS.BREAST_FEEDING}/${id}`);
  };

  const saveNote = (note) => {
    if (note) {
      dispatch(
        updateTrackingRecord(
          recordToEdit.id,
          recordToEdit.date,
          recordToEdit.quantity,
          recordToEdit.additionalInfo,
          note
        )
      );
      history.replace(`${RELATIVE_PAGE_PATHS.BREAST_FEEDING}`);
    }
  };

  const deleteNote = () => {
    dispatch(
      updateTrackingRecord(
        recordToEdit.id,
        recordToEdit.date,
        recordToEdit.quantity,
        recordToEdit.additionalInfo,
        ''
      )
    );
    history.replace(`${RELATIVE_PAGE_PATHS.BREAST_FEEDING}`);
  };

  const cancelNote = () => {
    setModal(MODAL_TYPE.DEFAULT);
    history.replace(RELATIVE_PAGE_PATHS.BREAST_FEEDING);
  };

  const checkUserIsSubscribedToPaidTrackers = () => {
    const canAdd = canAddTrackerRecord(
      isSubscribedToPaidStickers,
      records.length,
      RELATIVE_LINKS_PROD.BREAST_FEEDING
    );

    if (!canAdd) {
      const paidTrackerMsg = i18n.t(
        'trackers:trackingScreen:paid-trackers.not-paid-message'
      );
      dispatch(showPaidProductsModal(paidTrackerMsg));
    }

    return canAdd;
  };

  const setToEditDialog = async () => {
    const userAccess = checkUserIsSubscribedToPaidTrackers();
    if (!userAccess) return;

    setModal(MODAL_TYPE.EDIT);
  };

  return (
    <>
      <div className="flexContainerColumn flexContainerCentered">
        {renderLastRecord()}
        <TrackingTimer
          onSave={saveRecord}
          tracker={TRACKERS_SUB_TYPE.BREAST_FEEDING}
          checkUserIsSubscribedToPaidTrackers={
            checkUserIsSubscribedToPaidTrackers
          }
        />
        <div className="pb-3 d-flex justify-content-center">
          <Button
            type="submit"
            variant="outlined"
            onClick={() => setToEditDialog()}
            disabled={modal !== MODAL_TYPE.DEFAULT}
            className={
              !isSubscribedToPaidStickers &&
              !FREE_TRACKERS.includes(RELATIVE_LINKS_PROD.BREAST_FEEDING)
                ? 'disabledBtn'
                : ''
            }
          >
            {i18n.t('trackers:breastfeedingScreen:buttons.add')}
          </Button>
        </div>
        <TrackerHistory
          historyRecords={records}
          requiresSpecialEditing={true}
          trackerName={TRACKER_SUBTYPES.BREAST_FEEDING}
          onEdit={editRecord}
          onAdd={openNote}
          setModal={setModal}
        />
      </div>

      {modal === MODAL_TYPE.EDIT && (
        <TrackingEditDialog
          trackerType={TRACKERS_SUB_TYPE.BREAST_FEEDING}
          startTime={recordToEdit?.date}
          quantity={recordToEdit?.quantity}
          additionalInfo={recordToEdit?.additionalInfo || BREASTS.LEFT}
          onClose={closeRecord}
          onSave={saveRecord}
          isEdit={!!recordToEdit}
        />
      )}
      {modal === MODAL_TYPE.NOTE && (
        <TrackerRecordNote
          currentNote={recordToEdit.notes}
          onSave={saveNote}
          onCancel={cancelNote}
          onDelete={deleteNote}
        />
      )}
    </>
  );
};

const trackerIdSelector = (state) => {
  const tracker = (state.trackers || []).find(
    (x) => x.name === TRACKERS_SUB_TYPE.BREAST_FEEDING
  );
  return tracker ? tracker : -1;
};
